import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

const TermsOfService: React.FC = () => {
  return (
    <div className="terms-page fade-in">
      <div className="terms-container">
        <div className="terms-header">
          <h1>Terms of Service</h1>
          <p className="terms-subtitle">Please read these terms carefully before using BeanBuzz</p>
        </div>

        <div className="terms-content">
          <div className="terms-last-updated">
            <p>Last updated: March 1, 2026</p>
          </div>

          <div className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using BeanBuzz ("the Service"), operated by BeanBuzz ("we," "our," or
              "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree
              to these Terms, you may not access or use the Service.
            </p>
            <p>
              We reserve the right to update or modify these Terms at any time. Your continued use
              of the Service after any changes constitutes your acceptance of the revised Terms.
            </p>
          </div>

          <div className="terms-section">
            <h2>2. Description of Service</h2>
            <p>
              BeanBuzz is a cafe management platform that provides tools for cafe owners and
              managers to streamline their operations. The Service includes, but is not limited to:
            </p>
            <ul className="terms-list">
              <li>Menu creation and management</li>
              <li>Table configuration and QR code ordering</li>
              <li>Order processing and tracking</li>
              <li>Kitchen dashboard for real-time order management</li>
              <li>Customer-facing ordering interfaces</li>
              <li>Analytics and reporting tools</li>
              <li>Organization and cafe settings management</li>
            </ul>
            <p>
              The Service is currently in active development. Features may be added, modified, or
              removed at our discretion. We do not guarantee uninterrupted or error-free operation
              of the Service.
            </p>
          </div>

          <div className="terms-section">
            <h2>3. Account Registration</h2>
            <p>
              To access certain features of the Service, you must create an account. When creating
              an account, you agree to:
            </p>
            <ul className="terms-list">
              <li>Provide accurate, current, and complete registration information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p>
              You may register using your email address or through third-party authentication
              providers (e.g., Google Sign-In). You must be at least 16 years old to create an
              account.
            </p>
          </div>

          <div className="terms-section">
            <h2>4. User Responsibilities</h2>
            <p>As a user of the Service, you agree to:</p>
            <ul className="terms-list">
              <li>Use the Service only for lawful purposes and in accordance with these Terms</li>
              <li>Not use the Service to transmit any harmful, offensive, or illegal content</li>
              <li>Not attempt to gain unauthorized access to any part of the Service or its systems</li>
              <li>Not interfere with or disrupt the integrity or performance of the Service</li>
              <li>Not reverse engineer, decompile, or disassemble any aspect of the Service</li>
              <li>Not use automated systems or software to extract data from the Service</li>
              <li>Ensure that any data you input into the Service complies with applicable laws</li>
              <li>Maintain accurate and up-to-date information for your cafe, including menu items and pricing</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>5. Cafe Data and Content</h2>

            <h3>5.1 Your Content</h3>
            <p>
              You retain ownership of all data, content, and information you submit to the Service,
              including cafe details, menu items, and business configurations ("Your Content"). By
              submitting Your Content, you grant us a limited, non-exclusive license to use, store,
              and display Your Content solely for the purpose of providing the Service to you.
            </p>

            <h3>5.2 Data Accuracy</h3>
            <p>
              You are solely responsible for the accuracy of Your Content, including menu
              descriptions, pricing, allergen information, and any other details presented to your
              customers through the Service.
            </p>

            <h3>5.3 Data Backup</h3>
            <p>
              While we take reasonable measures to protect Your Content, we recommend that you
              maintain independent backups of your important business data. We are not liable for
              any loss of data.
            </p>
          </div>

          <div className="terms-section">
            <h2>6. Intellectual Property</h2>

            <h3>6.1 Our Intellectual Property</h3>
            <p>
              The Service, including its design, features, functionality, source code, and all
              associated intellectual property, is owned by BeanBuzz and is protected by copyright,
              trademark, and other intellectual property laws. You may not copy, modify, distribute,
              or create derivative works based on the Service without our express written permission.
            </p>

            <h3>6.2 Trademarks</h3>
            <p>
              "BeanBuzz" and associated logos are trademarks of BeanBuzz. You may not use our
              trademarks without prior written consent. Your use of the Service does not grant you
              any rights to our trademarks or branding.
            </p>

            <h3>6.3 Feedback</h3>
            <p>
              Any feedback, suggestions, or ideas you provide regarding the Service may be used by
              us without obligation to you. By submitting feedback, you grant us an unrestricted,
              perpetual, irrevocable license to use such feedback for any purpose.
            </p>
          </div>

          <div className="terms-section">
            <h2>7. Payment and Billing</h2>
            <p>
              BeanBuzz may offer free and paid subscription plans. If you select a paid plan:
            </p>
            <ul className="terms-list">
              <li>You agree to pay all applicable fees as described at the time of purchase</li>
              <li>Fees are non-refundable except as required by applicable law</li>
              <li>We reserve the right to change pricing with reasonable advance notice</li>
              <li>Payment processing is handled by secure third-party payment processors</li>
            </ul>
            <p>
              Failure to pay applicable fees may result in suspension or termination of your access
              to paid features.
            </p>
          </div>

          <div className="terms-section">
            <h2>8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law:
            </p>
            <ul className="terms-list">
              <li>
                The Service is provided "as is" and "as available" without warranties of any kind,
                either express or implied, including but not limited to warranties of
                merchantability, fitness for a particular purpose, and non-infringement
              </li>
              <li>
                We do not warrant that the Service will be uninterrupted, secure, or error-free
              </li>
              <li>
                We shall not be liable for any indirect, incidental, special, consequential, or
                punitive damages arising from your use of or inability to use the Service
              </li>
              <li>
                Our total liability for any claims arising from or related to the Service shall not
                exceed the amount you paid us in the twelve (12) months preceding the claim
              </li>
              <li>
                We are not responsible for any losses resulting from orders placed through the
                platform, including incorrect orders, pricing errors, or customer disputes
              </li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless BeanBuzz and its officers,
              directors, employees, and agents from and against any claims, liabilities, damages,
              losses, and expenses (including reasonable legal fees) arising out of or in any way
              connected with:
            </p>
            <ul className="terms-list">
              <li>Your access to or use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any applicable laws or regulations</li>
              <li>Any content you submit through the Service</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>10. Termination</h2>

            <h3>10.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by contacting us or using the account
              deletion features within the Service. Upon termination, your right to use the Service
              will immediately cease.
            </p>

            <h3>10.2 Termination by Us</h3>
            <p>
              We may suspend or terminate your account and access to the Service at any time,
              with or without cause, and with or without notice. Reasons for termination may
              include, but are not limited to:
            </p>
            <ul className="terms-list">
              <li>Violation of these Terms</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Non-payment of applicable fees</li>
              <li>Extended periods of inactivity</li>
              <li>Requests by law enforcement or government agencies</li>
            </ul>

            <h3>10.3 Effect of Termination</h3>
            <p>
              Upon termination, we may delete your account data in accordance with our data
              retention policies. Provisions of these Terms that by their nature should survive
              termination shall survive, including but not limited to intellectual property
              provisions, limitation of liability, and indemnification.
            </p>
          </div>

          <div className="terms-section">
            <h2>11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the
              Province of Ontario, Canada, without regard to its conflict of law provisions. Any
              disputes arising under or in connection with these Terms shall be subject to the
              exclusive jurisdiction of the courts located in Ontario, Canada.
            </p>
          </div>

          <div className="terms-section">
            <h2>12. General Provisions</h2>

            <h3>12.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy and any other legal notices published
              on the Service, constitute the entire agreement between you and BeanBuzz regarding
              your use of the Service.
            </p>

            <h3>12.2 Severability</h3>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, the remaining
              provisions shall continue in full force and effect.
            </p>

            <h3>12.3 Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms shall not constitute a
              waiver of such right or provision.
            </p>

            <h3>12.4 Assignment</h3>
            <p>
              You may not assign or transfer these Terms or your rights under them without our
              prior written consent. We may assign our rights and obligations under these Terms
              without restriction.
            </p>
          </div>

          <div className="terms-section">
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="terms-contact-card">
              <p><strong>Email:</strong>{' '}
                <a href="mailto:vadym@suprun.ca" className="terms-link">vadym@suprun.ca</a>
              </p>
              <p><strong>Subject:</strong> Terms of Service Inquiry</p>
            </div>
          </div>
        </div>

        <div className="terms-footer">
          <Link to="/" className="back-link">&larr; Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
